import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageShell, { PageHero, LightSection } from '@/components/PageShell';
import { FundTheGapCTA } from '@/components/content/FundTheGapCTA';
import { sportGuides } from '@/lib/data/sportGuides';

const TITLE = 'Fundraising Guides by Sport';
const DESCRIPTION =
  'Sport-by-sport fundraising guides for athletes and parents: what a club or travel season really costs, when the money is due, and fundraising ideas that fit the sport.';

export default function FundraisingGuidesIndex() {
  usePageMeta(TITLE, DESCRIPTION, {
    breadcrumbs: [{ name: 'Resources', path: '/resources' }, { name: 'Fundraising guides by sport' }],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: TITLE,
      itemListElement: sportGuides.map((g, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: `How to fundraise for ${g.noun}`,
        url: `https://thesportfund.com/resources/fundraising-guides/${g.slug}`,
      })),
    },
  });

  return (
    <PageShell
      hero={
        <PageHero
          eyebrow="Fundraising guides · By sport"
          title={
            <>
              Every sport has its own{' '}
              <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-300 bg-clip-text text-transparent">
                money calendar.
              </span>
            </>
          }
          lead="Club volleyball deposits land right after tryouts. Travel baseball's big bills hit in summer. Pick your sport for its real costs, its money moments, and fundraising ideas that fit."
          size="sm"
        />
      }
    >
      <LightSection>
        <div className="grid md:grid-cols-2 gap-6">
          {sportGuides.map((g) => (
            <Link
              key={g.slug}
              to={`/resources/fundraising-guides/${g.slug}`}
              className="group block rounded-3xl overflow-hidden border border-slate-200/70 bg-white hover:border-slate-300 hover:-translate-y-1 transition-all duration-300 shadow-sm hover:shadow-xl"
            >
              <div className="aspect-[16/8] overflow-hidden">
                <img src={g.image} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-6">
                <h2 className="text-2xl font-bold tracking-tight text-slate-900 group-hover:text-blue-700 transition-colors">
                  How to fundraise for {g.noun}
                </h2>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{g.description}</p>
                <p className="mt-3 text-sm text-slate-500">
                  Typical competitive season: <span className="font-semibold text-slate-800">{g.costRange}</span>
                </p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-blue-700 font-semibold text-sm">
                  Read the guide <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-12 max-w-3xl">
          <p className="text-slate-600">
            Don't see your sport? The general playbook —{' '}
            <Link to="/resources/how-to-fundraise-for-a-sports-season" className="text-blue-700 underline">
              7 honest ways to fundraise for a season
            </Link>{' '}
            — works for all of them, and we're adding sports as we verify their numbers.
          </p>
          <FundTheGapCTA />
        </div>
      </LightSection>
    </PageShell>
  );
}
